<script setup lang="ts">
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/user';
import Toast, { type ToastMessageOptions } from 'primevue/toast';
import { onMounted, onUnmounted, watch } from 'vue';
import { onMessageListener, subscribeLocalPush } from './services/firebase';
import { useAppStore } from './stores/app';
import { prepareToast, type AppToastMessage } from './models/toast';
import type { MessagePayload } from 'firebase/messaging';
import { useRouter } from 'vue-router';
import SpData from '@/assets/sp-data-icon.svg';
import { useToast } from 'primevue/usetoast';
import { DEFAULT_NOTIFICATION_TITLE, removeBootSkeleton, transformDeepLink } from './utils';
import { useNotificationStore } from './stores/notification';
import { NotificationModel } from './models/notification';
import { useI18n } from 'vue-i18n';
import { ErrorBoundary, ErrorScreen, ReportErrorDialog } from '@/components/errors';
import { useErrorStore } from './stores/error';

const { t: $t } = useI18n();

const errorStore = useErrorStore();
const authStore = useAuthStore();
authStore.checkAuth();
const appStore = useAppStore();
const toast = useToast();
const notificationStore = useNotificationStore();


const userStore = useUserStore();
if (authStore.userLoggedIn) {
  userStore.getUser(authStore.userData!.identifier!);
}

const router = useRouter();

/**
 * A fatal error replaces the router outlet *in place*, without navigating.
 *
 * That is the one way the perfusion dashboard's tier-0 silhouette can be left on screen: it is a
 * fixed overlay removed by `SurgeonDashboard` mounting or by a navigation going somewhere else,
 * and a fatal error is neither. `getStudies()` sets one on exactly the path a perfusion URL takes,
 * so without this the surgeon is left looking at a silhouette with the error screen sealed behind
 * it and nothing to click.
 */
watch(
  () => errorStore.fatal,
  fatal => {
    if (fatal) {
      removeBootSkeleton();
    }
  }
);

const viewDetails = (url: string | undefined, message: ToastMessageOptions) => {
  if (url) {
    router.replace(url);
    toast.remove(message);
  }
};


/**
 * PrimeVue's default icon per severity.
 *
 * The previous expression was `severity ? 'pi-check-circle' : 'pi-info-circle'`, and `severity` is
 * truthy for every real toast — so an error showed a green check.
 */
/** A toast carries only the reference, so the store looks the error back up by it. */
const reportFromToast = (message: ToastMessageOptions) => {
  const reference = (message as AppToastMessage).data?.reference;
  if (typeof reference === 'string') {
    errorStore.openReport(reference);
  }
  toast.remove(message);
};

const toastIcon = (severity?: string) => {
  switch (severity) {
    case 'error':
      return 'pi-times-circle';
    case 'warn':
      return 'pi-exclamation-triangle';
    case 'success':
      return 'pi-check-circle';
    default:
      return 'pi-info-circle';
  }
};

let closeForegroundPush: (() => void) | undefined;
let closeLocalPush: (() => void) | undefined;

/**
 * Foreground rendering for one push: an in-app toast rather than an OS notification.
 *
 * Shared by the real FCM listener and, in local development, by `subscribeLocalPush` — which routes
 * a push-inbox message here or to the service worker depending on tab visibility, exactly as FCM
 * splits between `onMessage` and `onBackgroundMessage`.
 */
const handleForegroundPush = (payload: MessagePayload) => {
  // A badge-only push (`{notification:{badge:n}}`, sent by the API's `updateBadge`) has no title and
  // no body. Testing `payload.notification` alone let it through, and the fallbacks below then
  // rendered a toast reading "notification title" / "notification body".
  if (payload.notification?.title || payload.notification?.body) {
    // The guard only rules out a push with *neither* field, so the fallbacks still have to be real
    // copy: a body-only push would otherwise show the placeholder title to the user. Resolved once
    // and reused below, so the notification-centre row reads exactly like the toast instead of
    // landing in the list with a blank title.
    const title = payload.notification?.title || DEFAULT_NOTIFICATION_TITLE;
    const body = payload.notification?.body ?? '';

    const toastData = prepareToast('success', title, body, 10000);

    toastData.closable = false;

    let finalUrl = undefined;
    if (payload.data?.url) {
      // Transform the incoming URL into the desired path format
      finalUrl = transformDeepLink(payload.data.url);
    }
    
    // Add a 'detail' object to signal a custom toast
    toastData.data = { 
      type: 'push-notification',
      // You can pass any other custom data here
      ...payload.data,
      url: finalUrl
    };

    appStore.addToast(toastData);

    // The toast is transient by design; the notification centre is where this
    // stays. Prepending it here means a push that arrives with the app open
    // shows up in the list and the badge immediately, rather than only after
    // the next poll.
    notificationStore.prependFromPush(
      NotificationModel.fromPushPayload(
        (payload.data ?? {}) as Record<string, string>,
        title,
        body
      )
    );
  }
};

onMounted(() => {
  // The unsubscribe is kept, not dropped: the root component survives for the life of the tab in
  // production, but an HMR reload or a remount in a test would otherwise stack a second FCM listener
  // and toast every push twice. Still floating, and still swallowing — push is a nice-to-have, and an
  // unhandled rejection here would reach the global handler and raise an error screen over a working page.
  onMessageListener(handleForegroundPush)
    .then(unsubscribe => {
      closeForegroundPush = unsubscribe;
    })
    .catch(() => {});

  // No-op outside dev; locally it stands in for the FCM delivery LocalStack cannot perform.
  subscribeLocalPush(handleForegroundPush).then(unsubscribe => {
    closeLocalPush = unsubscribe;
  });

  if ('serviceWorker' in navigator && navigator.serviceWorker?.addEventListener) {
    // Listen to service worker messages sent via postMessage()
    navigator.serviceWorker.addEventListener('message', event => {
      if (!event.data.action) {
        return;
      }

      switch (event.data.action) {
        case 'redirect-from-notificationclick':
          router.replace(event.data.url);
          break;
        // no default
      }
    });
  }
});

onUnmounted(() => {
  closeForegroundPush?.();
  closeLocalPush?.();
});
</script>

<template>
  <Toast>
    <template #message="slotProps">
      <!-- Check for the custom 'push-notification' type -->
      

      <div v-if="slotProps.message.data?.type === 'push-notification'" class="toast-wrapper">
        <div class="toast-accent"></div>

        <div class="toast-icon">
          <img alt="Studypages Data" :src="SpData" />
        </div>

        <div class="toast-content">
          <h3 class="toast-title">{{slotProps.message.summary}}</h3>
          <p class="toast-message">{{slotProps.message.detail}}</p>
          
          <button type="button"
            class="toast-action"
            v-if="slotProps.message.data?.url"
            @click="viewDetails(slotProps.message.data.url, slotProps.message)">
            View
          </button>
        </div>

        <button type="button"
          class="toast-close"
          aria-label="Close notification"
          @click="toast.remove(slotProps.message)">
          <FontAwesomeIcon :icon="['fas', 'close']" class="gallery-close" />
        </button>
      </div>

      <!-- An app error: same shape as the default toast, plus a way to report it. No reference code
           here — it appears on the error screen and in the report dialog, where it is worth writing down. -->
      <div v-else-if="slotProps.message.data?.type === 'app-error'" class="p-toast-message-content">
        <span class="p-toast-message-icon pi pi-times-circle"></span>
        <div class="p-toast-message-text">
          <div class="p-toast-summary">{{ slotProps.message.summary }}</div>
          <div class="p-toast-detail">{{ slotProps.message.detail }}</div>
          <button type="button"
            class="toast-action"
            v-if="slotProps.message.data?.canReport"
            @click="reportFromToast(slotProps.message)">
            {{ $t('error_report_this') }}
          </button>
        </div>
      </div>

      <!-- ELSE: Render a template that mimics the default PrimeVue toast -->
      <div v-else class="p-toast-message-content">
        <span :class="['p-toast-message-icon', 'pi', toastIcon(slotProps.message.severity)]"></span>
        <div class="p-toast-message-text">
          <div class="p-toast-summary">{{ slotProps.message.summary }}</div>
          <div class="p-toast-detail">{{ slotProps.message.detail }}</div>
        </div>
      </div>
    </template>
  </Toast>
  <ErrorScreen v-if="errorStore.fatal"
    :error="errorStore.fatal"
    @retry="errorStore.retryFatal()"
    @report="errorStore.openReport()"/>
  <ErrorBoundary v-else level="page">
    <RouterView />
  </ErrorBoundary>
  <ReportErrorDialog />
</template>

<style lang="scss">
/* Not using 'scoped' here, when using scoped properties for toast, they are not working. */
.p-toast {
  font-size: 14px;
}
.p-toast-message-text {
  margin: 0 0 0 0.5rem;
  line-height: 1.2;
}
.p-toast-summary {
  font-weight: 500;
}
.p-toast-message-icon {
  height: 16px;
  width: 16px;
}
.p-toast-detail {
  margin-top: 8px;
}
.p-toast-icon-close {
  height: 16px;
  width: 16px;
  outline-color: transparent;
}
.grecaptcha-badge { 
  visibility: hidden !important;
}
.p-galleria-close-button {
  z-index: 9999 !important;
  background-color: lightgray !important;
}
.toast-wrapper {
  position: relative;
  display: flex;
  align-items: flex-start;
  width: 100%;
  max-width: 500px;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 24px 32px 24px 28px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.toast-accent {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 60%;
  width: 6px;
  background-color: #3B82F6; /* Bright blue */
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

.toast-icon {
  flex-shrink: 0;
  margin-right: 16px;
  margin-top: -2px; /* Slight optical adjustment */
}

.toast-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.toast-title {
  margin: 0 0 6px 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.toast-message {
  margin: 0 0 12px 0;
  font-size: 15px;
  line-height: 1.5;
  color: #374151;
}

.toast-action {
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  font-size: 15px;
  color: #6B7280;
  text-decoration: underline;
  text-underline-offset: 4px;
  cursor: pointer;
  transition: color 0.2s ease;
}

.toast-action:hover {
  color: #374151;
}

.toast-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 26px;
  height: 26px;
  background-color: #F3F4F6;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.toast-close:hover {
  background-color: #E5E7EB;
}

.p-toast .p-toast-message:has(.toast-wrapper) {
  /* Remove background and border from the main message container */
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.p-toast .p-toast-message:has(.toast-wrapper) .p-toast-message-content {
  /* Remove padding from the inner content container */
  padding: 0 !important;
}

.p-toast-message-content {
  justify-content: space-between;
}
</style>
